import {Component, InputComponent, Object3D} from "@wonderlandengine/api";
import {property} from "@wonderlandengine/api/decorators.js";
import { UserSettingsStore } from "./utils/user-settings-store.js";
import { PlayerMovementHandler } from "./player-movement-handler.js";
/**
 * This component snaps the player to a fixed rotation when the trigger is
 * moved left or right.
 *
 * @remarks
 * The component is dependent on an {@link InputComponent} component on the same object.
 */
export class SnapRotate extends Component {
	static TypeName = "snap-rotate";
    static instances: Set<SnapRotate> = new Set<SnapRotate>();

	@property.object({required: true})
	private controller?: Object3D;

    @property.object()
    playerObject: Object3D | null = null;

    @property.float(45)
    private snapAngle: number = 45;

	private input?: InputComponent;
	private snapped = false;
	private lowThreshold = 0.2;
	private highThreshold = 0.5;
    private _playerMovementHandler: PlayerMovementHandler | null = null;

	init() {
		SnapRotate.instances.add(this);
	}

	onDestroy() {
		SnapRotate.instances.delete(this);
	}

	public getSnapAngle(): number {
		return this.snapAngle;
	}

	public setSnapAngle(angle: number): void {
		const safeAngle = Number.isFinite(angle) ? Math.max(0, angle) : this.snapAngle;
		this.snapAngle = safeAngle;
	}

	public setControlActive(active: boolean): void {
		this.active = active;
	}

	start() {
		const persistedAngle = UserSettingsStore.getNumber(UserSettingsStore.Keys.SnapRotationAngle);
		if (persistedAngle != null) {
			this.setSnapAngle(persistedAngle);
		}

		const persistedMode = UserSettingsStore.getRotationMode('snap');
		this.setControlActive(persistedMode === 'snap');

		const tempInput = this.controller?.getComponent(InputComponent);
		if (!tempInput) {
			throw new Error(
				"The snap-rotate component requires an input component on controller object",
			);
		}

		const playerMovementSource = this.playerObject ?? this.object;
		const tempPlayerMove = playerMovementSource.getComponent(PlayerMovementHandler);
		if (!tempPlayerMove) {
			throw new Error(
				"The snap-rotate component requires a player-movement-handler component on playerObject (or on this object if playerObject is unset)",
			);
		}
		this._playerMovementHandler = tempPlayerMove;

		this.input = tempInput;
		this.snapped = false;
	}

	queue = new Array<() => void>();

	update() {
		if (!this.active) {
			this.snapped = false;
			return;
		}

		if (!this.input?.xrInputSource?.gamepad?.axes?.length) {
			return;
		}
		const exec = this.queue.pop();
		if (exec) {
			exec();
			return;
		}
		const currentAxis = this.input.xrInputSource.gamepad.axes[2];
		if (Math.abs(currentAxis) < this.lowThreshold) {
			this.snapped = false;
			return;
		}
		// need to release trigger before snapping again
		if (this.snapped || Math.abs(currentAxis) < this.highThreshold) {
			return;
		}
		const angle = this.snapAngle;
		if (currentAxis < -this.highThreshold && !this.snapped) {
			this._playerMovementHandler?.requestSnapRotate(angle);
			this.snapped = true;
		}

		if (currentAxis > this.highThreshold && !this.snapped) {
			this._playerMovementHandler?.requestSnapRotate(-angle);
			this.snapped = true;
		}
	}
}
